import { useLayoutEffect, type RefObject } from 'react';
import { prefersReducedMotion } from '@lib/utils/motion';
import type { ExitKind, ghostOut as play } from './ghostOut';

export type { ExitKind } from './ghostOut';

/** The exit animation once its chunk is in (ghostOut.ts). */
let exit: typeof play | null = null;
let loading: Promise<void> | null = null;
/**
 * Fetches the exit animation the first time an overlay mounts (never under reduced motion: nothing would play), so it
 * is in by the time that overlay closes. A failed fetch is tried again by the next overlay; meanwhile overlays simply
 * leave at once.
 */
function loadExit() {
  if (exit || loading || prefersReducedMotion()) return;
  loading = import('./ghostOut').then(
    (m) => {
      exit = m.ghostOut;
    },
    () => {
      loading = null;
    },
  );
}

/**
 * The exit animation of an overlay React removes at once (a still, inert copy fades out where it was: ghostOut.ts),
 * played once its chunk is in; before that, the overlay simply leaves at once.
 */
export function ghostOut(el: HTMLElement | null | undefined, kind: ExitKind) {
  if (exit) exit(el, kind);
  else loadExit();
}

/** `ghostOut(ref.current)` when the component holding `ref` unmounts. */
export function useExitGhost(ref: RefObject<HTMLElement | null>, kind: ExitKind) {
  useLayoutEffect(() => {
    loadExit();
    return () => ghostOut(ref.current, kind);
  }, [ref, kind]);
}

/**
 * The same for a part rendered conditionally (`{open && …}`): put `<ExitGhost of={ref} kind />` BEFORE that element in
 * the same conditional, so its cleanup runs while the element is still in the page.
 */
export function ExitGhost({ of, kind }: { of: RefObject<HTMLElement | null>; kind: ExitKind }) {
  useExitGhost(of, kind);
  return null;
}
